import {
  notesInterface,
  singleConceptInterface,
  singleContractInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';
import {
  forecastInstanceInterface,
  invoiceSalesSummary,
  unconstrainedForecastInterface,
} from 'Interfaces/FDPInterfaces/unconstrainedForecast';
import { makeAutoObservable, runInAction } from 'mobx';
import { unconstrainedForecastService } from 'services/fdp/unconstrainedForecastService';
import { tmsScreenServices } from 'services/tmsScreenServices';
import { deepCopy } from 'utils/deepCopy';

export class ConstrainedForecastEditStore {
  directcurrentNDCIndex: number = 0;
  indirectcurrentNDCIndex: number = 0;
  isConceptLoading: boolean = false;
  conceptDetails: singleConceptInterface | null = {
    concept: '',
    gfc: 0,
    generic_rxcui: '',
    therapeutic_category: '',
    pharmacologic_class: '',
    va_class_name: '',
    des_schedule: '',
    duration_condition: '',
    Strength: '',
  };
  contractDetails: singleContractInterface[] | [] = [
    {
      GFC: 0,
      ContractNumber: '',
      ContractType: '',
      StartDate: '',
      EndDate: '',
    },
  ];

  isConstrainedForecastDataLoading: boolean = false;
  // unconstrainedForecastData: unconstrainedForecastInterface = [];
  directNDCheading: { ndc: string; packSize: string }[] = [];
  indirectNDCheading: { ndc: string; packSize: string }[] = [];
  constrainedForecastEdit: unconstrainedForecastInterface = [];
  oldconstrainedForecastEdit: unconstrainedForecastInterface = [];

  isInvoiceSalesLoading: boolean = false;
  invoiceSalesSummary: invoiceSalesSummary[] = [];
  isConstrainedForecastNotesDataLoading: boolean = false;
  ConstrainedForecastNotesData: notesInterface[] = [];
  isConstrainedNoteSaved: Set<string> = new Set();
  isConstrainedNoteSaving: boolean = false;
  ndcsForWhichOldValuesDisabled: Set<string> = new Set();
  lastNDCInput: {
    ndc: string;
    val: string;
  }[] = [];
  isDirectMode: boolean = true;

  constructor() {
    makeAutoObservable(this);
  }

  setIsDirectMode = (val: boolean) => {
    this.isDirectMode = val;
  };

  updateOldValues = () => {
    this.oldconstrainedForecastEdit = deepCopy(this.constrainedForecastEdit);
  };

  initializeLastInput = (ndc: string, val: string) => {
    this.lastNDCInput.push({ ndc: ndc, val: val });
  };

  setDirectCurrentTabIndex = (newIndex: number) => {
    this.directcurrentNDCIndex = newIndex;
  };

  setIndirectCurrentTabIndex = (newIndex: number) => {
    this.indirectcurrentNDCIndex = newIndex;
  };

  setLastNDCInput = (ndc: string, lastValue: string) => {
    const lastData = this.lastNDCInput.map(item =>
      item.ndc === ndc ? { ...item, val: lastValue } : item
    );
    this.lastNDCInput = { ...lastData };
  };

  getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    return `${year}-${month}-01`;
  };

  handleGetConceptDetails = async (gfc: number) => {
    runInAction(() => {
      this.isConceptLoading = true;
    });
    try {
      const data = await tmsScreenServices.getSingleGSMSConcept(gfc);
      runInAction(() => {
        this.conceptDetails = data.concept_info[0];
        this.contractDetails = data.contract_info;
      });

      const planning_period = this.getCurrentDate();
      this.getUnconstrainedForecastViewData(String(gfc), planning_period);
      this.getInvoiceSalesSummary(gfc);
    } catch (error) {
      console.error('Error while getting concept details:', error);
    } finally {
      runInAction(() => {
        this.isConceptLoading = false;
      });
    }
  };

  fetchForecastGFCNotes = async (ndc11: string, signal?: AbortSignal) => {
    this.isConstrainedForecastNotesDataLoading = true;
    try {
      this.ConstrainedForecastNotesData = await unconstrainedForecastService.getNDC11Note(
        ndc11,
        'constrained',
        signal
      );
    } catch (error) {
      console.log('MSPlanStore fetchMSGFCNotes => ', error);
    } finally {
      this.isConstrainedForecastNotesDataLoading = false;
    }
  };

  setNoteData = (idx: number, note: string) => {
    this.ConstrainedForecastNotesData[idx].Notes = note;
  };

  removeNoteData = (idx: number) => {
    console.log('remove data');
    this.ConstrainedForecastNotesData.splice(idx, 1);
  };

  saveForecastNote = async (ndc11: string, note: string, planning_type: string) => {
    this.isConstrainedNoteSaving = true;
    let data: notesInterface[] = [];
    try {
      const payload = {
        ndc11: ndc11,
        note: note,
        planning_type: planning_type,
      };
      data = await unconstrainedForecastService.saveNDC11Note(payload);
      this.isConstrainedNoteSaved.add(ndc11);
    } catch (error) {
      console.log('Error saving Note=> ', error);
    } finally {
      this.ConstrainedForecastNotesData = data;
      this.isConstrainedNoteSaving = false;
    }
  };

  getInvoiceSalesSummary = async (gfc: number) => {
    this.isInvoiceSalesLoading = true;
    try {
      const response = await unconstrainedForecastService.getSalesSummaryDetails(gfc);
      this.invoiceSalesSummary = response;
    } catch (error) {
      console.log('getInvoiceSalesSummary=> ', error);
    } finally {
      this.isInvoiceSalesLoading = false;
    }
  };

  getUnconstrainedForecastViewData = async (gfc: string, planning_period: string) => {
    this.isConstrainedForecastDataLoading = true;
    try {
      const response = await unconstrainedForecastService.getUnconstrainedForecast(
        gfc,
        planning_period
      );
      this.constrainedForecastEdit = response;
      this.oldconstrainedForecastEdit = response;
      this.fetchNDCHeadings();
    } catch (error) {
      console.log('getUnconstrainedForecastViewData=> ', error);
    } finally {
      this.isConstrainedForecastDataLoading = false;
    }
  };

  fetchNDCHeadings = () => {
    this.directNDCheading = [];
    this.indirectNDCheading = [];
    const tempNdcsForWhichOldValuesDisabled: Set<string> = new Set();

    for (let i = 1; i <= 16; i++) {
      const dt = new Date();
      dt.setMonth(dt.getMonth() + i);

      // filter unconstrained forecast data
      for (const item of this.constrainedForecastEdit) {
        if (item.SellingCategory && item.SellingCategory.toLowerCase() === 'direct') {
          if (this.directNDCheading.filter(heading => heading.ndc === item.NDC11).length === 0) {
            this.directNDCheading.push({
              ndc: item.NDC11,
              packSize: String(item.PackSize),
            });
          }
        } else if (item.SellingCategory && item.SellingCategory.toLowerCase() === 'indirect') {
          if (this.indirectNDCheading.filter(heading => heading.ndc === item.NDC11).length === 0) {
            this.indirectNDCheading.push({
              ndc: item.NDC11,
              packSize: String(item.PackSize),
            });
          }
        }
      }
    }

    if (this.directNDCheading[0]) {
      this.setIsDirectMode(true);
      this.fetchForecastGFCNotes(this.directNDCheading[0]?.ndc);
    } else {
      this.setIsDirectMode(false);
      this.fetchForecastGFCNotes(this.indirectNDCheading[0]?.ndc);
    }

    this.ndcsForWhichOldValuesDisabled = tempNdcsForWhichOldValuesDisabled;
  };

  updateConstrainedForecastEdit = async (
    unconstraintForecastDetailsParticularNdcMonthForecast: forecastInstanceInterface,
    ndcIndex: number,
    forecastMonthIndex: number
  ) => {
    if (
      ndcIndex < this.constrainedForecastEdit.length &&
      this.constrainedForecastEdit[ndcIndex].forecast_data.length > forecastMonthIndex
    ) {
      this.constrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex] =
        unconstraintForecastDetailsParticularNdcMonthForecast;
    }
  };

  resetStore = () => {
    // Tab and layout state
    this.directcurrentNDCIndex = 0;
    this.indirectcurrentNDCIndex = 0;
    this.isDirectMode = true;

    // Loading states
    this.isConceptLoading = false;
    this.isConstrainedForecastDataLoading = false;
    this.isInvoiceSalesLoading = false;
    this.isConstrainedForecastNotesDataLoading = false;
    this.isConstrainedNoteSaving = false;

    // Concept and contract details
    this.conceptDetails = {
      concept: '',
      gfc: 0,
      generic_rxcui: '',
      therapeutic_category: '',
      pharmacologic_class: '',
      va_class_name: '',
      des_schedule: '',
      duration_condition: '',
      Strength: '',
    };
    this.contractDetails = [
      {
        GFC: 0,
        ContractNumber: '',
        ContractType: '',
        StartDate: '',
        EndDate: '',
      },
    ];

    // NDC data
    this.directNDCheading = [];
    this.indirectNDCheading = [];
    this.lastNDCInput = [];

    // Forecast data
    this.constrainedForecastEdit = [];
    this.oldconstrainedForecastEdit = [];

    // Notes and sales data
    this.invoiceSalesSummary = [];
    this.ConstrainedForecastNotesData = [];
    this.isConstrainedNoteSaved = new Set();
    this.ndcsForWhichOldValuesDisabled = new Set();
  };
}
